const AUTH_ERROR_LABELS: Record<string, string> = {
  invalid_credentials: 'Неверный email или пароль.',
  brs_unreachable: 'Сервис авторизации недоступен. Попробуйте позже.',
  brs_bad_response: 'Сервис авторизации вернул некорректный ответ.',
  brs_not_configured: 'Авторизация не настроена. Обратитесь к администратору.',
  INVALID_TENANT: 'Укажите разрешённый домен организации.',
  TOO_MANY_ATTEMPTS: 'Слишком много попыток. Попробуйте через 15 минут.',
  MISSING_CREDENTIALS: 'Введите email и пароль.',
  unknown: 'Не удалось войти. Попробуйте еще раз.',
};

interface BrsLoginInput {
  email: string;
  password: string;
  remember: boolean;
  tenant: string;
  /**
   * Куда вернуть пользователя после успешного входа. Может быть:
   *   • относительным путём (например, `/dashboard`) — навигация внутри launcher
   *   • абсолютным URL в `*.zafrm.pro` — SSO-redirect на metrics/market
   * Любое другое значение бэкенд отбрасывает на `/`.
   */
  next?: string;
}

type BrsLoginResult =
  | { ok: true; redirect: string }
  | { ok: false; message: string };

export function mapAuthError(code: string | undefined): string {
  return AUTH_ERROR_LABELS[code ?? 'unknown'] ?? AUTH_ERROR_LABELS.unknown;
}

export function validateBrsCredentials(email: string, password: string): string | null {
  const normalizedEmail = email.trim();
  if (!normalizedEmail) return 'Введите email.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    return 'Введите корректный email.';
  }
  if (!password) return 'Введите пароль.';
  return null;
}

const TENANT_DOMAIN_SUFFIX = '.zafrm.ru';

export function validateTenantLabel(tenantLabel: string): string | null {
  if (!tenantLabel.trim()) return 'Введите домен организации.';
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(tenantLabel.trim())) {
    return 'Введите домен третьего уровня без .zafrm.ru.';
  }
  return null;
}

export function tenantLabelToDomain(tenantLabel: string): string {
  return `${tenantLabel.trim().toLowerCase()}${TENANT_DOMAIN_SUFFIX}`;
}

export function tenantDomainToLabel(tenantDomain: string): string {
  const normalized = tenantDomain.trim().toLowerCase();
  return normalized.endsWith(TENANT_DOMAIN_SUFFIX)
    ? normalized.slice(0, -TENANT_DOMAIN_SUFFIX.length)
    : normalized;
}

export async function loginWithBrs(input: BrsLoginInput): Promise<BrsLoginResult> {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: input.email.trim(),
        password: input.password,
        remember: input.remember,
        tenant: input.tenant.trim().toLowerCase(),
        next: input.next,
      }),
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return { ok: false, message: mapAuthError(body?.error) };
    }

    const body = (await res.json().catch(() => null)) as { redirect?: string } | null;
    return { ok: true, redirect: body?.redirect ?? '/' };
  } catch {
    return { ok: false, message: AUTH_ERROR_LABELS.brs_unreachable };
  }
}

export async function logoutFromLauncher(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).catch(() => undefined);
}
